import React, { useState, useEffect } from 'react';
import { Clock, User, AlertCircle, Loader } from 'lucide-react';
import apiClient from '../services/apiClient';

/**
 * LastEditInfo - Sprint 2 Component
 * 
 * Componente para mostrar información de la última edición del submission.
 * Muestra quién editó, cuándo, y el tiempo transcurrido en formato legible.
 * 
 * Features:
 * - Auto-actualización cada 30 segundos
 * - Loading state mientras carga
 * - Display mode: inline | card
 * - Formateo de tiempo relativo (hace X minutos/horas/días)
 * 
 * Props:
 * - submissionId: string (requerido) - ID del submission
 * - display: 'inline' | 'card' - Modo de visualización
 * - autoRefresh: boolean - Actualizar automáticamente
 * - className: string - Clases CSS adicionales
 * 
 * Uso:
 * ```jsx
 * <LastEditInfo 
 *   submissionId="SUB-0001" 
 *   display="inline" 
 *   autoRefresh={true}
 * />
 * ```
 */

const LastEditInfo = ({ 
  submissionId, 
  display = 'inline', 
  autoRefresh = true,
  className = '' 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editInfo, setEditInfo] = useState(null);
  
  // Cargar información de última edición
  const loadLastEdit = async () => {
    try {
      setError(null);
      const response = await apiClient.get(`/api/student-execution/submissions/${submissionId}/last-edit`);
      
      if (response.data.success && response.data.data.hasEdits) {
        setEditInfo(response.data.data);
      } else {
        setEditInfo(null);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error loading last edit info:', err);
      setError('Error al cargar información de edición');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadLastEdit();
    
    // Auto-refresh cada 30 segundos
    if (autoRefresh) {
      const interval = setInterval(loadLastEdit, 30000);
      return () => clearInterval(interval);
    }
  }, [submissionId, autoRefresh]);
  
  // Estados de carga y error
  if (loading) {
    return (
      <div className={`flex items-center gap-2 text-slate-500 text-sm ${className}`}>
        <Loader className="w-3 h-3 animate-spin" />
        <span>Cargando...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`flex items-center gap-2 text-red-500 text-sm ${className}`}>
        <AlertCircle className="w-3 h-3" />
        <span>{error}</span>
      </div>
    );
  }
  
  if (!editInfo) {
    return (
      <div className={`flex items-center gap-2 text-slate-400 text-sm ${className}`}>
        <Clock className="w-3 h-3" />
        <span>Sin ediciones recientes</span>
      </div>
    );
  }
  
  // Mapeo de acciones a texto legible
  const actionLabels = {
    b2_updated: 'editó el formulario B2',
    comment_added: 'agregó un comentario',
    comment_updated: 'actualizó su comentario',
    manual_save: 'guardó cambios',
    declared: 'declaró el entregable',
    status_changed: 'cambió el estado'
  };
  
  const actionText = actionLabels[editInfo.action] || editInfo.description;
  
  // Render en modo "inline" (compacto)
  if (display === 'inline') {
    return (
      <div className={`flex items-center gap-2 text-sm text-slate-600 ${className}`}>
        <Clock className="w-3.5 h-3.5 text-slate-400" />
        <span>
          <span className="font-medium text-slate-700">
            {editInfo.user?.nombre || 'Alguien'}
          </span>
          {' '}
          {actionText}
          {' '}
          <span className="text-slate-500">
            {editInfo.timeAgo?.toLowerCase()}
          </span>
        </span>
      </div>
    );
  }
  
  // Render en modo "card" (detallado)
  return (
    <div className={`bg-slate-50 border border-slate-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
            <User className="w-4 h-4 text-slate-600" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-slate-900 text-sm">
              {editInfo.user?.nombre || 'Usuario desconocido'}
            </span>
            <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-100 rounded-full">
              {editInfo.user?.email}
            </span>
          </div>
          
          <p className="text-sm text-slate-600 mb-1">
            {actionText}
          </p>
          
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Clock className="w-3 h-3" />
            <span>{editInfo.timeAgo}</span>
            {editInfo.timestamp && (
              <>
                <span className="mx-1">•</span>
                <span>
                  {new Date(editInfo.timestamp).toLocaleString('es-ES', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LastEditInfo;
